import { inject } from "@angular/core";
import { CanActivateFn, Router, ActivatedRouteSnapshot } from "@angular/router";
import { SessionService } from "../services/session.service";
import { QPilotService } from "../services/qpilot.service";
import { map } from "rxjs";

/**
 * Guard to check if Quick Actions feature flag is enabled for a site
 *
 * This guard checks if the site has the "enable-quickactions" feature flag.
 * If it doesn't, the user is redirected to the access-denied page.
 */
export const quickActionsFeatureGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
) => {
  const sessionService = inject(SessionService);
  const qpilotService = inject(QPilotService);
  const router = inject(Router);

  // Get siteId from route params (usually from parent route) or query params (for standalone routes)
  const siteId =
    route.paramMap.get("siteId") ||
    route.parent?.paramMap.get("siteId") ||
    route.queryParamMap.get("siteId");

  if (!siteId || !sessionService.accessToken) {
    console.warn(
      "No siteId or access token available for quickActionsFeatureGuard",
    );
    return false;
  }

  // Fetch feature flags and check if quick actions is enabled
  return qpilotService
    .getSiteEnabledFeatureFlags(Number(siteId), sessionService.accessToken)
    .pipe(
      map((featureFlags: string[]) => {
        const isEnabled = featureFlags.includes("enable-quickactions");

        if (!isEnabled) {
          console.warn("Quick Actions feature is not enabled for this site.");
          // Redirect to access-denied page with reason and siteId
          void router.navigate(["/access-denied"], {
            queryParams: {
              reason: "quick-actions-disabled",
              siteId: siteId,
            },
          });
          return false;
        }

        return true;
      }),
    );
};
